// --- IMPORTS -------------------------------
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import '../report.css';
import ApiService from '../../../../services/apiService';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
const numberFmt = (n, digits = 2) =>
  new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number.isFinite(+n) ? +n : 0);

const dateToInputValue = (d) => new Date(d).toISOString().slice(0, 10); // just yyyy-mm-dd

const COLUMNS = [
  { key: 'PeriodDisplay', label: 'Date', width: 160 },
  { key: 'TypeName', label: 'Type', width: 120 },
  { key: 'Quantity', label: 'QTY', align: 'right' },
  { key: 'Transactions', label: 'Transactions', align: 'right' },
  { key: 'Revenue', label: 'Total Sale Amount', align: 'right' },
];

// ⭐ GENERIC TEMPLATE
const ReportTemplate = ({
  reportType, // e.g. 'Sales Summary', 'Sales By Hour', ...
  pageTitle, // title shown on screen
  csvFilePrefix, // e.g. 'sales-summary'
  measure = 'Revenue', // if you ever need to change this per report
  Currency,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalRecords: 0,
    totalPages: 0,
  });

  // Default last 10 days
  const now = new Date();
  const tenDaysAgo = new Date(now);
  tenDaysAgo.setDate(now.getDate() - 10);

  const [formData, setFormData] = useState({
    fromDate: dateToInputValue(tenDaysAgo),
    toDate: dateToInputValue(now),
  });

  // ------------------------------------------------------------
  // Fetch Branches
  // ------------------------------------------------------------
  const fetchBranches = async () => {
    try {
      const apiService = new ApiService();
      const res = await apiService.get('GET_BRANCH');

      const list = res?.data?.result || [];

      const mapped = list.map((b) => ({
        value: b.id,
        label: b.branchName,
      }));

      setBranches(mapped);
    } catch (e) {
      console.error('Failed to fetch branches', e);
    }
  };

  // ------------------------------------------------------------
  // Fetch Report Data (ReportType is dynamic)
  // ------------------------------------------------------------
  const fetchData = async (page = pagination.currentPage, size = pagination.pageSize) => {
    setLoading(true);
    setErr('');

    try {
      const apiService = new ApiService();
      const params = new URLSearchParams();

      params.append('PageNo', page);
      params.append('PageSize', size);
      params.append('ReportType', reportType); // ⭐ dynamic
      params.append('Measure', measure); // still configurable
      params.append('StartDate', formData.fromDate);
      params.append('EndDate', formData.toDate);
      params.append('Comparison', 'No Comparison');
      params.append('BranchId', selectedBranch?.value || 0); // 0 = all branches

      const res = await apiService.getApiParamater('GET_SALE_SUMMERY', `?${params.toString()}`);

      const result = Array.isArray(res?.data?.result) ? res.data.result : [];

      // If all report types return same fields, you can keep this mapper.
      const normalized = result.map((r) => ({
        PeriodDisplay: r.PeriodDisplay || r.PeriodDate || '—',
        TypeName: r.TypeName || '—',
        Quantity: +r.Quantity || 0,
        Transactions: +r.Transactions || 0,
        Revenue: +r.Revenue || 0,
      }));

      setRows(normalized);

      setPagination((p) => ({
        ...p,
        ...(res?.data?.paginationData || {}),
        totalRecords: normalized.length,
        currentPage: page,
        pageSize: size,
      }));
    } catch (e) {
      console.error(e);
      setErr('Failed to fetch report. Please try again.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  // Sorting
  const [sortState, setSortState] = useState({ key: 'PeriodDisplay', dir: 'asc' });

  const sortedRows = useMemo(() => {
    const { key, dir } = sortState;
    const copy = [...rows];

    copy.sort((a, b) => {
      const av = a[key];
      const bv = b[key];

      if (Number.isFinite(+av) && Number.isFinite(+bv)) {
        return dir === 'asc' ? av - bv : bv - av;
      }

      const ad = new Date(av);
      const bd = new Date(bv);

      if (!isNaN(ad) && !isNaN(bd)) {
        return dir === 'asc' ? ad - bd : bd - ad;
      }

      return dir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });

    return copy;
  }, [rows, sortState]);

  const totals = useMemo(
    () => ({
      qty: sortedRows.reduce((s, r) => s + r.Quantity, 0),
      trans: sortedRows.reduce((s, r) => s + r.Transactions, 0),
      rev: sortedRows.reduce((s, r) => s + r.Revenue, 0),
    }),
    [sortedRows],
  );

  const onChangeRange = (e) => setFormData((old) => ({ ...old, [e.target.name]: e.target.value }));

  const onSearch = () => fetchData(1, pagination.pageSize);

  const onSort = (key) => setSortState((prev) => (prev.key !== key ? { key, dir: 'asc' } : { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }));

  // CSV Export
  const exportCSV = () => {
    const headers = COLUMNS.map((c) => `"${c.label}"`).join(',');
    const body = sortedRows.map((r) => COLUMNS.map((c) => `"${String(r[c.key]).replace(/"/g, '""')}"`).join(',')).join('\n');

    const csv = `${headers}\n${body}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `${csvFilePrefix}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };
  const exportExcel = () => {
    if (!sortedRows.length) return;

    const header = COLUMNS.map((c) => c.label);

    const body = sortedRows.map((r) =>
      COLUMNS.map((c) => {
        const v = r?.[c.key];

        // keep numeric columns numeric for Excel
        if (c.key === 'Quantity') return Number(v) || 0;
        if (c.key === 'Transactions') return Number(v) || 0;
        if (c.key === 'Revenue') return Number(v) || 0;

        return String(v ?? '').trim();
      }),
    );

    const totalsRow = ['Total', '', Number(totals.qty) || 0, Number(totals.trans) || 0, Number(totals.rev) || 0];

    const aoa = [header, ...body, totalsRow];

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // Optional widths (uses COLUMNS width if provided)
    ws['!cols'] = COLUMNS.map((c, idx) => ({
      wch: c.width ? Math.max(10, Math.round(c.width / 7)) : idx === 0 ? 18 : 14,
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, pageTitle || 'Report');

    const branchName = selectedBranch?.label ? selectedBranch.label.replace(/[\\/:*?"<>|]/g, '-') : 'All-Branches';
    const date = new Date().toISOString().slice(0, 10);

    XLSX.writeFile(wb, `${csvFilePrefix}_${branchName}_${formData.fromDate}_to_${formData.toDate}_${date}.xlsx`);
  };
  return (
    <div className="p-5">
      <button className="addItem-btn mb-3" onClick={() => navigate(-1)}>
        ← Back
      </button>

      {/* <h4 className="mb-3">{pageTitle}</h4> */}

      {/* Filters */}
      <div className="row g-3 align-items-end">
        <div className="col-md-3">
          <label className="form-label">Select Branch</label>
          <Select
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 999999 }),
              menu: (base) => ({ ...base, zIndex: 999999 }),
            }}
            value={selectedBranch}
            onChange={(opt) => setSelectedBranch(opt)}
            options={branches}
            placeholder="Select Branch"
            isClearable
          />
        </div>

        <div className="col-md-3">
          <label className="form-label">From Date</label>
          <input type="date" className="form-control" name="fromDate" value={formData.fromDate} onChange={onChangeRange} />
        </div>

        <div className="col-md-3">
          <label className="form-label">To Date</label>
          <input type="date" className="form-control" name="toDate" value={formData.toDate} onChange={onChangeRange} />
        </div>

        <div className="col-md-3 d-flex gap-3 align-items-end">
          <button className="addItem-btn" onClick={onSearch} disabled={loading}>
            {loading ? 'Loading…' : 'Search'}
          </button>

          <button className="btn btn-outline-secondary" onClick={exportCSV} disabled={!sortedRows.length}>
            Export CSV
          </button>

          <button className="btn btn-outline-success" onClick={exportExcel} disabled={!sortedRows.length}>
            Export Excel
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="POsgridshad rounded p-0 mt-4">
        {err && <div className="alert alert-danger m-3">{err}</div>}

        <div className="custom-table-container">
          <table className="custom-table">
            <thead>
              <tr>
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    onClick={() => onSort(c.key)}
                    className={c.align === 'right' ? 'text-end' : ''}
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    {c.label}
                    {sortState.key === c.key ? (sortState.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {!loading && sortedRows.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length} className="text-center p-4">
                    No data found
                  </td>
                </tr>
              )}

              {!loading &&
                sortedRows.map((r, index) => (
                  <tr key={index}>
                    <td>{r.PeriodDisplay}</td>
                    <td>{r.TypeName}</td>
                    <td className="text-end">{r.Quantity}</td>
                    <td className="text-end">{r.Transactions}</td>
                    <td className="text-end">
                      {Currency?.currency?.code} {numberFmt(r.Revenue)}
                    </td>
                  </tr>
                ))}

              {loading && (
                <tr>
                  <td colSpan={COLUMNS.length} className="text-center p-4">
                    Loading…
                  </td>
                </tr>
              )}
            </tbody>

            {sortedRows.length > 0 && (
              <tfoot>
                <tr className="fw-bold bg-light">
                  <td colSpan={2}>Total</td>
                  <td className="text-end">{totals.qty}</td>
                  <td className="text-end">{totals.trans}</td>
                  <td className="text-end">
                    {Currency?.currency?.code} {numberFmt(totals.rev)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => state;
export default connect(mapStateToProps)(ReportTemplate);
